import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Descriptions, Skeleton, Tag } from 'antd'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { RENT_UNITS, RENTAL_STATUS } from './const/rental-list-search.ts'
import { OrderDetailSection } from './order-detail-section.tsx'
import { RentalContractSummary } from './rental-contract-summary.tsx'
import { formatDate, getPropertyAddress, getSubjectLabel } from './utils/rental-detail.ts'

export function RentalDetailSummary({ order }: { order: RentalOrder }) {
  return (
    <section className="border-border bg-surface overflow-hidden rounded-xl border shadow-xs">
      <div className="bg-surface-muted border-border flex flex-wrap items-start justify-between gap-4 border-b px-5 py-5 sm:px-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Tag color={RENTAL_STATUS[order.status].color}>{RENTAL_STATUS[order.status].text}</Tag>
            {order.archivedAt && order.status !== 'archived' && <Tag>已归档</Tag>}
            <span className="text-content-muted text-sm">租赁订单</span>
          </div>
          <h2 className="text-content mt-3 mb-1 text-lg font-semibold">
            {order.siteName} {order.roomNo ? `· ${order.roomNo}` : ''}
          </h2>
          <p className="text-content-muted m-0 text-sm">
            {formatDate(order.startAt)} 至 {formatDate(order.endAt)} · {order.organizationName}
          </p>
        </div>
        <div className="border-border bg-surface rounded-lg border px-4 py-3 text-right">
          <span className="text-content-soft block text-xs">订单 ID</span>
          <span className="text-content mt-1 block font-mono text-sm font-semibold">#{order.id}</span>
        </div>
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        <OrderDetailSection title="租赁物业">
          <Descriptions
            className="[&_table]:w-full"
            classNames={{ content: 'min-w-0 break-words' }}
            column={{ xs: 1, sm: 2, lg: 3 }}
            items={[
              { key: 'site', label: '租赁场地', children: order.siteName },
              { key: 'owner', label: '出租经营主体', children: order.organizationName },
              {
                key: 'address',
                label: '完整地址',
                children: getPropertyAddress(order.addressSnapshot, order.roomNo),
                span: 'filled'
              }
            ]}
          />
        </OrderDetailSection>
        <OrderDetailSection title="订单归属与租赁约定">
          <Descriptions
            className="[&_table]:w-full"
            classNames={{ content: 'min-w-0 break-words' }}
            column={{ xs: 1, sm: 2, lg: 3 }}
            items={[
              { key: 'customer', label: '客户', children: order.customerName },
              ...(order.renewedFromId
                ? [
                    {
                      key: 'renewedFrom',
                      label: '续租来源',
                      children: <a href={`/orders/rentals/${order.renewedFromId}`}>原订单 #{order.renewedFromId}</a>
                    }
                  ]
                : []),
              ...(order.terminatedAt
                ? [{ key: 'terminated', label: '实际截止日（不含）', children: formatDate(order.terminatedAt) }]
                : []),

              { key: 'subject', label: '订单当次资料', children: getSubjectLabel(order) },
              { key: 'group', label: '订单组', children: order.orderGroupName || '未加入' },
              {
                key: 'period',
                label: '合同租期',
                children: `${formatDate(order.startAt)} 至 ${formatDate(order.endAt)}`
              },
              {
                key: 'mode',
                label: '使用方式',
                children: order.occupancyMode === 'physical' ? '实际入驻' : '注册地址'
              },
              { key: 'sales', label: '业务员', children: order.salespersonName },
              {
                key: 'price',
                label: '协议价',
                children: `¥${formatAmount(order.agreedPrice)}/${RENT_UNITS[order.rentType]}`
              },
              {
                key: 'interval',
                label: '出账频率',
                children: `每 ${order.billingInterval} ${RENT_UNITS[order.rentType]}`
              },
              {
                key: 'managementFee',
                label: '管理费',
                children:
                  order.managementFee === null
                    ? '未约定'
                    : `¥${formatAmount(order.managementFee)}/${RENT_UNITS[order.rentType]}`
              },
              {
                key: 'deposit',
                label: '约定押金',
                children: order.depositAmount === null ? '未约定' : `¥${formatAmount(order.depositAmount)}`
              },
              {
                key: 'water',
                label: '水费单价',
                children: order.waterUnitPrice ? `¥${formatAmount(order.waterUnitPrice)} / 吨` : '未约定'
              },
              {
                key: 'electricity',
                label: '电费单价',
                children: order.electricityUnitPrice ? `¥${formatAmount(order.electricityUnitPrice)} / 度` : '未约定'
              },
              { key: 'org', label: '经营主体', children: order.organizationName },
              { key: 'party', label: '签约方', children: order.contractPartyName || '—' }
            ]}
          />
        </OrderDetailSection>

        <OrderDetailSection title="当前合同">
          <RentalCurrentContract rentalOrderId={order.id} />
        </OrderDetailSection>
      </div>
    </section>
  )
}

function RentalCurrentContract({ rentalOrderId }: { rentalOrderId: number }) {
  const trpc = useTRPC()
  const access = useAccess()
  const query = useQuery({
    ...trpc.order.rental.currentContract.queryOptions({ rentalOrderId }),
    enabled: access.can('contract.document.read')
  })
  if (!access.can('contract.document.read')) return <p className="text-content-muted">暂无合同查看权限</p>
  if (query.isPending) return <Skeleton active paragraph={{ rows: 2 }} />
  if (query.isError)
    return (
      <Alert
        type="error"
        title={query.error.message}
        action={<Button onClick={() => void query.refetch()}>重试</Button>}
      />
    )
  if (!query.data) return <p className="text-content-muted">尚未保存有效合同</p>
  return <RentalContractSummary document={query.data} />
}
